import { useEffect, useState, useMemo } from 'react';

import { Modal, Table, Button, message, Tag, Spin } from 'antd';

import type { Board } from './hooks/useBoards';
import { useCategories } from './hooks/useCategories';
import { useCategoryPermissionStatus } from './hooks/useCategoryPermissions';
import { getCategoryPermissions } from '../../api/adminAPI';
import CategoryRoleModal from './CategoryRoleModal';

interface BoardRoleModalProps {
    open: boolean;
    board: Board | null;
    onClose: () => void;
    onSubmit: (updates: any[]) => void;
    isSubmitting?: boolean;
}

const BoardRoleModal = ({ open, board, onClose, onSubmit, isSubmitting = false }: BoardRoleModalProps) => {
    const rootCategoryId = board?.rootCategories?.[0]?.id;
    const { categories, loading: categoriesLoading, loadedRootId } = useCategories(rootCategoryId);
    const { usersStatus, loading: usersLoading, loadedParentId } = useCategoryPermissionStatus(rootCategoryId);

    // 현재 board(rootCategoryId)에 해당하는 카테고리/유저 데이터가 모두 로드되었는지.
    // 보드 전환 시 이전 보드의 데이터를 잘못 사용하는 것을 방지한다.
    const isDataReady =
        rootCategoryId !== undefined &&
        loadedRootId === rootCategoryId &&
        loadedParentId === rootCategoryId;

    const [initialCategoryRoles, setInitialCategoryRoles] = useState<Record<number, number[]>>({});
    const [userCategoryRoles, setUserCategoryRoles] = useState<Record<number, number[]>>({});
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any | null>(null);
    const [isFetchingPermissions, setIsFetchingPermissions] = useState(false);
    // '권한 상태' 컬럼에 카테고리 목록을 표시하기 위해, 열릴 때 전체 유저의 권한 카테고리를 미리 조회
    const [isPrefetching, setIsPrefetching] = useState(false);

    // 카테고리 id -> 이름 매핑
    const categoryNameById = useMemo(() => {
        const map = new Map<number, string>();
        categories.forEach((c: any) => map.set(c.id, c.name));
        return map;
    }, [categories]);

    const sortedUsers = useMemo(() => {
        if (!usersStatus) return [];

        return [...usersStatus].sort((a, b) => {
            const isAdminA = a.role === 'ADMIN_MAIN' || a.role === 'ADMIN_SUB';
            const isAdminB = b.role === 'ADMIN_MAIN' || b.role === 'ADMIN_SUB';

            if (isAdminA && !isAdminB) return 1;
            if (!isAdminA && isAdminB) return -1;
            if (isAdminA && isAdminB) return 0;

            const statusOrder: Record<string, number> = {
                'ALL': 1,
                'PARTIAL': 2,
                'NONE': 3
            };

            const orderA = statusOrder[a.permissionStatus] || 4;
            const orderB = statusOrder[b.permissionStatus] || 4;

            return orderA - orderB;
        });
    }, [usersStatus]);

    const handleOpenCategoryModal = async (user: any) => {
        setSelectedUser(user);
        setIsCategoryModalOpen(true);

        if (!initialCategoryRoles[user.userId] && rootCategoryId) {
            setIsFetchingPermissions(true);
            try {
                const res = await getCategoryPermissions(user.userId, rootCategoryId);
                const categoryData = res?.data || [];
                const checkedIds = categoryData
                    .filter((item: any) => item.hasPermission === true)
                    .map((item: any) => item.categoryId);
                setInitialCategoryRoles(prev => ({ ...prev, [user.userId]: checkedIds }));
                setUserCategoryRoles(prev => ({ ...prev, [user.userId]: checkedIds }));
            } catch (error) {
                message.error('유저의 기존 카테고리 권한을 불러오는데 실패했습니다.');
            } finally {
                setIsFetchingPermissions(false);
            }
        }
    };

    const handleCategoryRoleSave = (userId: number, checkedCategoryIds: number[]) => {
        setUserCategoryRoles(prev => ({
            ...prev,
            [userId]: checkedCategoryIds
        }));
        setIsCategoryModalOpen(false);
        setSelectedUser(null);
    };

    const handleSubmit = () => {
        if (!board) return;
        const updates: any[] = [];
        Object.entries(userCategoryRoles).forEach(([userIdStr, currentIds]) => {
            const userId = Number(userIdStr);
            const initialIds = initialCategoryRoles[userId] || [];

            const added = currentIds.filter(id => !initialIds.includes(id));
            const removed = initialIds.filter(id => !currentIds.includes(id));

            if (added.length > 0 || removed.length > 0) {
                updates.push({
                    boardId: board.board_id,
                    userId,
                    added,
                    removed,
                    remaining: currentIds, // 변경 후 최종적으로 보유하는 카테고리 목록
                });
            }
        });

        if (updates.length === 0) {
            message.info('변경된 권한이 없습니다.');
            return;
        }

        onSubmit(updates);
    };

    const handleClose = () => {
        setUserCategoryRoles({});
        setInitialCategoryRoles({});
        onClose();
    };

    const getDisabledStyle = (role: string) => {
        return (role === 'ADMIN_MAIN' || role === 'ADMIN_SUB') 
            ? { color: '#bfbfbf' } 
            : {};
    };

    // 모달이 열리거나 다른 보드로 전환되면(rootCategoryId 변경) 유저별 권한 상태를 초기화한다.
    // 상태는 userId로 키잉되어 보드 간 공유되므로, 전환 시 반드시 비워야 이전 보드 값이 남지 않는다.
    useEffect(() => {
        if (open) {
            setUserCategoryRoles({});
            setInitialCategoryRoles({});
        }
    }, [open, rootCategoryId]);

    // 모달이 열리고 카테고리/유저 로딩이 끝나면, 각 유저의 권한 카테고리 목록을 미리 조회한다.
    // (ALL -> 전체 카테고리, NONE -> 없음, PARTIAL -> 개별 조회)
    useEffect(() => {
        if (!open || !rootCategoryId) return;
        if (categoriesLoading || usersLoading) return;
        // 현재 board의 데이터가 확실히 로드된 뒤에만 prefetch (보드 전환 중 stale 데이터로 실행 방지)
        if (!isDataReady) return;
        if (sortedUsers.length === 0) return;

        let cancelled = false;
        const prefetch = async () => {
            setIsPrefetching(true);
            const allIds = categories.map((c: any) => c.id);
            const entries: Record<number, number[]> = {};
            await Promise.all(
                sortedUsers.map(async (u: any) => {
                    if (u.role === 'ADMIN_MAIN' || u.role === 'ADMIN_SUB') return;
                    if (u.permissionStatus === 'ALL') {
                        entries[u.userId] = allIds;
                        return;
                    }
                    if (u.permissionStatus === 'NONE') {
                        entries[u.userId] = [];
                        return;
                    }
                    try {
                        const res = await getCategoryPermissions(u.userId, rootCategoryId);
                        const data = res?.data || [];
                        entries[u.userId] = data
                            .filter((item: any) => item.hasPermission === true)
                            .map((item: any) => item.categoryId);
                    } catch {
                        entries[u.userId] = [];
                    }
                })
            );
            if (!cancelled) {
                setInitialCategoryRoles((prev) => ({ ...entries, ...prev }));
                setIsPrefetching(false);
            }
        };
        prefetch();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, rootCategoryId, isDataReady, categoriesLoading, usersLoading, sortedUsers, categories]);

    const columns = [
        {
            title: '아이디',
            dataIndex: 'username',
            key: 'username',
            width: 110,
            render: (text: string, record: any) => (
                <span style={getDisabledStyle(record.role)}>{text}</span>
            )
        },
        {
            title: '이름',
            dataIndex: 'name',
            key: 'name',
            width: 100,
            render: (text: string, record: any) => (
                <span style={getDisabledStyle(record.role)}>{text}</span>
            )
        },
        {
            title: '접근 권한',
            key: 'action',
            width: 130,
            align: 'center' as const,
            render: (_: any, record: any) => {
                if (record.role === 'ADMIN_MAIN' || record.role === 'ADMIN_SUB') {
                    return <span style={{ color: '#bfbfbf' }}>-</span>;
                }

                const currentChecked = userCategoryRoles[record.userId];
                const isEdited = currentChecked !== undefined;

                return (
                    <Button
                        size="small"
                        onClick={() => handleOpenCategoryModal(record)}
                        type={isEdited ? "primary" : "default"}
                    >
                        권한 관리 {isEdited ? `(${currentChecked.length})` : ''}
                    </Button>
                );
            }
        },
        {
            title: '권한 상태 (카테고리)',
            key: 'permissionStatus',
            render: (_: any, record: any) => {
                if (record.role === 'ADMIN_MAIN' || record.role === 'ADMIN_SUB') {
                    return <span style={{ color: '#bfbfbf' }}>관리자</span>;
                }

                // 편집값(userCategoryRoles) 우선, 없으면 미리 조회한 초기값(initialCategoryRoles)
                const ids = userCategoryRoles[record.userId] ?? initialCategoryRoles[record.userId];

                if (ids === undefined) {
                    return (isPrefetching || !isDataReady)
                        ? <Spin size="small" />
                        : <span style={{ color: '#bfbfbf' }}>-</span>;
                }
                if (ids.length === 0) {
                    return <span style={{ color: '#999' }}>권한 없음</span>;
                }

                const isAll = categories.length > 0 && ids.length >= categories.length;
                if (isAll) {
                    return <Tag color="blue">전체 권한</Tag>;
                }

                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {ids.map((id) => (
                            <Tag key={id} color="green" style={{ margin: 0 }}>
                                {categoryNameById.get(id) ?? id}
                            </Tag>
                        ))}
                    </div>
                );
            }
        },
    ];

    return (
        <>
            <Modal
                title={`게시판 카테고리 권한 관리 - ${board?.name || ''}`}
                open={open}
                onCancel={handleClose}
                width={1000}
                centered
                footer={[
                    <Button key="cancel" onClick={handleClose} disabled={isSubmitting}>취소</Button>,
                    <Button
                        key="submit"
                        type="primary"
                        onClick={handleSubmit}
                        loading={isSubmitting}
                    >
                        {isSubmitting ? '저장 중...' : `저장`}
                    </Button>,
                ]}
            >
                <Table
                    rowKey="userId"
                    columns={columns}
                    dataSource={sortedUsers}
                    loading={usersLoading || isSubmitting}
                    pagination={false}
                    scroll={{ y: 400 }}
                    size="small"
                />
            </Modal>

            <CategoryRoleModal
                open={isCategoryModalOpen}
                user={selectedUser}
                categories={categories}
                loading={categoriesLoading || isFetchingPermissions}
                initialChecked={selectedUser ? (userCategoryRoles[selectedUser.userId] ?? initialCategoryRoles[selectedUser.userId] ?? []) : []}
                onClose={() => {
                    setIsCategoryModalOpen(false);
                    setSelectedUser(null);
                }}
                onSave={handleCategoryRoleSave}
            />
        </>
    );
};

export default BoardRoleModal;